import EventSystem from "@/service/EventSystem";
import { PermutiveData } from "@/service/Permutive";
import TrackingService from "@/service/Tracking";

import PostMessageService from "./PostMessage";

export type HansIdResponse = {
  hansId: string;
  consent: boolean;
};

export type ParticipationTokenResponse = {
  status: "authenticated" | "unauthenticated" | "unavailable";
  participationToken?: string;
  expiresIn?: number;
};

export type initializedResponse = {
  inViewport: boolean;
  accessibilityModeEnabled: boolean;
};

const inViewport = false;
const postMessageService = new PostMessageService(window.parent);

const getTealiumConfig = () => {
  postMessageService.send({ message: "getTealiumConfig" });
  postMessageService.listener("getTealiumConfig", (data: any) => {
    if (data) {
      EventSystem.publish("tealium:received", data);
    }
  });
};

const getCanonical = () => {
  postMessageService.send<string>({ message: "getCanonical" }, (url: string) =>
    EventSystem.publish("canonical:received", url)
  );
};

const getHansId = () => {
  postMessageService.send<HansIdResponse>(
    { message: "getHansId" },
    (data: HansIdResponse) => EventSystem.publish("hansId:received", data)
  );
};

const getParticipationToken = () => {
  postMessageService.send<ParticipationTokenResponse>(
    { message: "getParticipationToken" },
    (data: ParticipationTokenResponse) =>
      EventSystem.publish("participationToken:received", data)
  );
};

const redirectToLogin = () => {
  postMessageService.send({ message: "redirectToLogin" });
};

const sendPermutiveDataToAdLib = (data: PermutiveData) => {
  postMessageService.send({
    message: "iat_permutive",
    data,
  });
};

// sends a post message event to la-hyper to trigger the outro overlay
const openOutroOverlay = (interactionId: string) => {
  postMessageService.send({
    message: "openOutroOverlay",
    data: { interactionId },
  });
};

const resize = (height: number) => {
  if (window.location.hash.indexOf("amp=1") === -1) {
    // Amp calls iframe with param
    postMessageService.send({ message: "resize", data: height });
  } else {
    // Send amp resize
    postMessageService.sendAmpResize(height);
  }
  // WELT News App
  postMessageService.sendAppResize(height);
};

const destroy = () => {
  postMessageService.send({ message: "destroy" });
};

const scroll = () => {
  postMessageService.send({ message: "scrollTo" });
};

const hasScrolledListener = () => {
  postMessageService.listener("parentHasScrolled", (data: any) =>
    EventSystem.publish("parent:scroll:finished", data)
  );
};

const reportAnswerAction = () => {
  postMessageService.send({ message: "interactionEvent" });
};

const initialized = () => {
  postMessageService.send({ message: "initialized" });
  onUpdateProps((data: initializedResponse) => {
    EventSystem.publish("hyper:ping", data);
    if (data.inViewport && !inViewport) {
      TrackingService.getInstance().trackOnInViewport();
    }
  });
};

export default {
  getTealiumConfig,
  getCanonical,
  getHansId,
  getParticipationToken,
  redirectToLogin,
  resize,
  destroy,
  initialized,
  scroll,
  hasScrolledListener,
  reportAnswerAction,
  sendPermutiveDataToAdLib,
  openOutroOverlay,
};

/**
 * @func onUpdateProps
 * @param listener
 */
function onUpdateProps(
  listener: (props: { inViewport: boolean }, iframe: HTMLIFrameElement) => void
): void {
  postMessageService.listener("updateProps", listener);
}
